import React, { useRef, useState } from 'react';
import {
  IconCheck,
  IconEye,
  IconEyeOff,
  IconLock,
  IconMail,
  IconRefresh,
  IconUser,
  IconX,
} from '@tabler/icons-react';
import { useForm } from 'react-hook-form';
import type { Partner } from '../../../entities/partner';
import { pb } from '../../../shared/api/pocketbase';
import { getPbErrorMessage } from '../../../shared/lib/pbError';
import { logAction } from '../../../shared/lib/userLogger';
import { Button } from '../../../shared/ui/Button';
import { EMPTY_PARTNER_FORM, type PartnerForm } from '../model/types';

const generatePassword = (length = 12): string => {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghjkmnpqrstuvwxyz';
  const digits = '23456789';
  const special = '!@#$%&*';
  const all = upper + lower + digits + special;
  const rand = () => crypto.getRandomValues(new Uint32Array(1))[0];
  let pwd =
    upper[rand() % upper.length] +
    lower[rand() % lower.length] +
    digits[rand() % digits.length] +
    special[rand() % special.length];
  for (let i = 4; i < length; i++) pwd += all[rand() % all.length];
  return pwd
    .split('')
    .sort(() => (rand() % 2 ? 1 : -1))
    .join('');
};

interface PartnerModalProps {
  mode: 'add' | 'edit';
  initial?: Partner | null;
  onClose: () => void;
  onSaved: (updated?: Partner) => void;
}

const PartnerModal: React.FC<PartnerModalProps> = ({ mode, initial, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
  } = useForm<PartnerForm>({
    defaultValues:
      mode === 'edit' && initial
        ? {
            name: initial.name ?? '',
            email: initial.email,
            password: ''
    }
        : EMPTY_PARTNER_FORM,
  });

  const handleFormSubmit = handleSubmit(async values => {
    setError('');
    setSaving(true);
    try {
      if (mode === 'add') {
        const partner = await pb.collection('partners').create({
          name: values.name.trim(),
          email: values.email.trim(),
          password: values.password,
          passwordConfirm: values.password
        });
        await pb.collection('partners').update(partner.id, { emailVisibility: true });
        logAction(`Created partner: ${values.email.trim()}`, 'ADD');
        onSaved();
      } else {
        const data: Record<string, unknown> = {
          name: values.name.trim(),
          email: values.email.trim(),
          emailVisibility: true,
        };
        if (values.password.trim()) {
          data.password = values.password;
          data.passwordConfirm = values.password;
        }
        await pb.collection('partners').update(initial!.id, data);
        logAction(`Edited partner: ${values.email.trim()}`, 'EDIT');
        onSaved({
          ...initial!,
          name: values.name.trim(),
          email: values.email.trim()
        });
      }
      onClose();
    } catch (err) {
      setError(getPbErrorMessage(err));
    } finally {
      setSaving(false);
    }
  });

  return (
    <div className="mo open" ref={overlayRef} onClick={e => e.target === overlayRef.current && onClose()}>
      <div className="cm cm-create" style={{ maxWidth: 480, overflow: 'visible' }}>
        <div className="cm-top">
          <div className="cm-ti">{mode === 'add' ? 'Добавить партнёра' : 'Редактировать партнёра'}</div>
          <div className="cm-sp" />
          <button className="cm-x" onClick={onClose} type="button">
            <IconX size={16} stroke={2} />
          </button>
        </div>
        <form className="cm-create-body" onSubmit={handleFormSubmit} style={{ gap: 14, overflow: 'visible' }}>
          <div className="adm-field">
            <label className="adm-label">Имя</label>
            <div className="adm-input-wrap">
              <IconUser size={15} stroke={2} className="adm-input-icon" />
              <input
                className="adm-input"
                type="text"
                placeholder="Название партнёра"
                autoComplete="off"
                {...register('name')}
              />
            </div>
          </div>
          <div className="adm-field">
            <label className="adm-label">
              Email <span className="adm-req">*</span>
            </label>
            <div className="adm-input-wrap">
              <IconMail size={15} stroke={2} className="adm-input-icon" />
              <input
                className="adm-input"
                type="email"
                placeholder="partner@example.com"
                autoComplete="off"
                {...register('email', {
                  required: 'Email обязателен',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Введите корректный email',
                  },
                })}
              />
            </div>
            {errors.email?.message ? <div className="form-field-error">{errors.email.message}</div> : null}
          </div>
          <div className="adm-field">
            <label className="adm-label">
              Пароль{mode === 'add' && <span className="adm-req"> *</span>}
              {mode === 'edit' && <span className="adm-hint"> — оставьте пустым, чтобы не менять</span>}
            </label>
            <div className="adm-input-wrap">
              <IconLock size={15} stroke={2} className="adm-input-icon" />
              <input
                className="adm-input adm-input-has-suffix"
                type={showPwd ? 'text' : 'password'}
                placeholder={mode === 'add' ? 'Минимум 8 символов' : '••••••••'}
                autoComplete="new-password"
                {...register('password', {
                  validate: value => {
                    if (mode === 'add' && value.length < 8) return 'Пароль минимум 8 символов';
                    if (mode === 'edit' && value && value.length < 8) return 'Пароль минимум 8 символов';
                    return true;
                  },
                })}
              />
              <div className="adm-input-suffix">
                <button
                  type="button"
                  className="adm-pwd-btn"
                  onClick={() => setShowPwd(v => !v)}
                  title={showPwd ? 'Скрыть' : 'Показать'}
                >
                  {showPwd ? <IconEyeOff size={15} stroke={2} /> : <IconEye size={15} stroke={2} />}
                </button>
                <button
                  type="button"
                  className="adm-pwd-btn adm-pwd-gen"
                  title="Сгенерировать пароль"
                  onClick={() => {
                    const p = generatePassword();
                    setValue('password', p, { shouldValidate: true });
                    setShowPwd(true);
                  }}
                >
                  <IconRefresh size={15} stroke={2} />
                </button>
              </div>
            </div>
            {errors.password?.message ? <div className="form-field-error">{errors.password.message}</div> : null}
          </div>
          {error && <div className="adm-form-error">{error}</div>}
          <div className="adm-form-actions">
            <Button variant="adm-cancel" type="button" onClick={onClose}>
              Отмена
            </Button>
            <Button variant="adm-save" type="submit" disabled={saving}>
              {saving ? <span className="adm-btn-spinner" /> : <IconCheck size={15} stroke={2.5} />}
              {mode === 'add' ? 'Создать' : 'Сохранить'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export { PartnerModal };
